import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Interfaces
// ═══════════════════════════════════════════════════════════════

export interface BrevoSyncEjecucion {
  id: number;
  tipo: string;
  estado: string;
  inicio: string;
  fin?: string;
  faseATotal?: number;
  faseAExitos: number;
  faseAErrores: number;
  faseBTotal?: number;
  faseBExitos: number;
  faseBErrores: number;
  duplicadosFusionados: number;
  parametros?: string;
  reset: boolean;
  dryRun: boolean;
  fases: string;
  errorDetalle?: string;
  /** automatica o manual, del propio registro (#236). */
  origen: string;
  /** Quién la lanzó; vacío en las automáticas. */
  usuarioAlta?: string;
  /** El latido del motor: la última vez que escribió algo en la ejecución. */
  ultimoLatido?: string;
  cancelacionPedida: boolean;
  ahora: string;
}

export interface BrevoSyncEstadoResponse {
  running?: BrevoSyncEjecucion;
  ultimaFinalizada?: BrevoSyncEjecucion;
}

export interface BrevoSyncHistoricoItem {
  id: number;
  tipo: string;
  estado: string;
  inicio: string;
  fin?: string;
  faseATotal?: number;
  faseAExitos: number;
  faseAErrores: number;
  faseBTotal?: number;
  faseBExitos: number;
  faseBErrores: number;
  duplicadosFusionados: number;
  parametros?: string;
  reset: boolean;
  dryRun: boolean;
  fases: string;
  errorDetalle?: string;
  duracionSegundos: number;
  origen: string;
  usuarioAlta?: string;
  ultimoLatido?: string;
  cancelacionPedida: boolean;
}

export interface BrevoSyncHistoricoResponse {
  ejecuciones: BrevoSyncHistoricoItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BrevoSyncRitmoPunto {
  minuto: string;
  contactos: number;
}

export interface BrevoSyncRitmoResponse {
  id: number;
  fase: string;
  puntos: BrevoSyncRitmoPunto[];
}

export interface BrevoSyncLanzarResponse {
  id: number;
  estado: string;
  inicio: string;
}

export interface HistoricoFilters {
  desde?: string;
  hasta?: string;
  estado?: string;
  tipo?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

// ═══════════════════════════════════════════════════════════════
// Cálculos derivados (spec §5.6)
// ═══════════════════════════════════════════════════════════════

export type FaseActual = 'loading' | 'A' | 'transition' | 'B';

export function computeFaseActual(r: BrevoSyncEjecucion): FaseActual {
  if (r.faseATotal == null) return 'loading';
  const aDone = r.faseAExitos + r.faseAErrores;
  if (aDone < r.faseATotal) return 'A';
  if (r.faseBTotal == null) return 'transition';
  return 'B';
}

// La base guarda las fechas en UTC y el backend las da como tales (#245); la pantalla las enseña en hora de Madrid.
const MADRID = 'Europe/Madrid';

/** «20/10/2026 10:00», en hora de Madrid. */
export function fmtFechaHoraMadrid(iso: string): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('es-ES', {
      timeZone: MADRID, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(iso)).map(x => [x.type, x.value]),
  );
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** «20/10/2026, 10:00:00», en hora de Madrid. */
export const fmtFechaHoraSegundosMadrid = (iso: string): string => new Date(iso).toLocaleString('es-ES', { timeZone: MADRID });

/** «10:00», en hora de Madrid. */
export const fmtHoraMadrid = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-ES', { timeZone: MADRID, hour: '2-digit', minute: '2-digit' });

/**
 * Lo que el histórico tiene que ver del estado (#247): la ejecución viva, su estado y su avance, y la última acabada.
 * Cuando cambia, el histórico se pone al día: al lanzar, mientras dura y al terminar.
 */
export function huellaHistorico(estado: BrevoSyncEstadoResponse | null): string {
  const r = estado?.running;
  const uf = estado?.ultimaFinalizada;
  return [
    r ? `${r.id}:${r.estado}:${r.faseAExitos + r.faseAErrores}:${r.faseBExitos + r.faseBErrores}:${r.cancelacionPedida}` : '-',
    uf ? `${uf.id}:${uf.estado}` : '-',
  ].join('|');
}

/** Lo que puede tardar el motor en arrancar en frío, y algo de margen: hasta entonces, una «Pendiente» solo espera (#245). */
export const ESPERA_ARRANQUE_SEGUNDOS = 10 * 60;

export function transcurridoSegundos(r: { inicio: string; ahora: string }): number {
  return Math.floor((new Date(r.ahora).getTime() - new Date(r.inicio).getTime()) / 1000);
}

export function porcentajeFase(total: number | undefined, exitos: number, errores: number): number | null {
  if (total == null || total === 0) return null;
  return Math.round(((exitos + errores) / total) * 100);
}

export function estimacionRestanteSegundos(r: BrevoSyncEjecucion): number | null {
  const fase = computeFaseActual(r);
  if (fase === 'loading') return null;

  const elapsed = transcurridoSegundos(r);
  if (elapsed < 30) return null;

  const aDone = r.faseAExitos + r.faseAErrores;
  const aTotal = r.faseATotal ?? 0;
  const bDone = r.faseBExitos + r.faseBErrores;
  const bTotal = r.faseBTotal ?? 0;

  const totalEstimado = aTotal + (bTotal || aTotal * 0.05);
  const hechoEstimado = aDone + bDone;

  if (hechoEstimado === 0) return null;
  const ratio = hechoEstimado / totalEstimado;
  if (ratio >= 1) return 0;

  return Math.round(elapsed / ratio - elapsed);
}

export function formatDuracion(segundos: number): string {
  if (segundos < 60) return `${segundos} s`;
  const min = Math.floor(segundos / 60);
  const sec = segundos % 60;
  if (min < 60) return sec > 0 ? `${min} min ${sec} s` : `${min} min`;
  const hrs = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin > 0 ? `${hrs}h ${remMin} min` : `${hrs}h`;
}

// ═══════════════════════════════════════════════════════════════
// Funciones API
// ═══════════════════════════════════════════════════════════════

export const getEstado = async (): Promise<BrevoSyncEstadoResponse> => {
  try {
    const response = await api.get('/api/brevo/sync/estado');
    return response.data;
  } catch (error) {
    console.error('Error fetching brevo sync estado:', error);
    throw error;
  }
};

export const getHistorico = async (filters: HistoricoFilters = {}): Promise<BrevoSyncHistoricoResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters.desde) params.append('desde', filters.desde);
    if (filters.hasta) params.append('hasta', filters.hasta);
    if (filters.estado) params.append('estado', filters.estado);
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.q) params.append('q', filters.q);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
    const query = params.toString();
    const response = await api.get(`/api/brevo/sync/historico${query ? '?' + query : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching brevo sync historico:', error);
    throw error;
  }
};

export const getEjecucion = async (id: number): Promise<BrevoSyncHistoricoItem> => {
  try {
    const response = await api.get(`/api/brevo/sync/ejecucion/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching brevo sync ejecucion ${id}:`, error);
    throw error;
  }
};

export const getRitmo = async (id: number, fase: string = 'A'): Promise<BrevoSyncRitmoResponse> => {
  try {
    const response = await api.get(`/api/brevo/sync/ritmo/${id}?fase=${fase}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching brevo sync ritmo ${id}:`, error);
    throw error;
  }
};

/** Las opciones de un lanzamiento, como valores cerrados (#235); la diaria de siempre es la de Marketing. */
export interface BrevoSyncLanzarParams {
  tipo: 'diario' | 'inicial' | 'setup_atributos' | 'seed_control';
  reset: boolean;
  dryRun: boolean;
  fases: 'AB' | 'A' | 'B';
}

export const DIARIA_DE_SIEMPRE: BrevoSyncLanzarParams = { tipo: 'diario', reset: true, dryRun: false, fases: 'AB' };

/** Las opciones como las dice la pantalla: «--reset --dry-run --solo-fase-a». */
export const textoOpciones = (p: Pick<BrevoSyncLanzarParams, 'reset' | 'dryRun' | 'fases'>): string =>
  [p.reset && '--reset', p.dryRun && '--dry-run', p.fases === 'A' && '--solo-fase-a', p.fases === 'B' && '--solo-fase-b']
    .filter(Boolean).join(' ');

export const lanzarSync = async (params: BrevoSyncLanzarParams = DIARIA_DE_SIEMPRE): Promise<BrevoSyncLanzarResponse> => {
  try {
    const response = await api.post('/api/brevo/sync/lanzar', params);
    return response.data;
  } catch (error) {
    console.error('Error launching brevo sync:', error);
    throw error;
  }
};

export interface BrevoSyncPararResponse {
  id: number;
  /** running mientras el motor atiende la parada; error si no había empezado o ya no daba señales. */
  estado: string;
}

/** Pide que pare una ejecución viva (#235): el motor la atiende entre lotes. */
export const pedirParada = async (id: number, motivo: string): Promise<BrevoSyncPararResponse> => {
  try {
    const response = await api.post(`/api/brevo/sync/parar/${id}`, { motivo });
    return response.data;
  } catch (error) {
    console.error(`Error stopping execution ${id}:`, error);
    throw error;
  }
};

export interface BrevoSyncLogResponse {
  id: number;
  logPath: string;
  contenido: string;
  lineasTotales: number;
  lineasDevueltas: number;
  truncado: boolean;
}

export const getLog = async (id: number, lineas: number = 200, soloErrores: boolean = false): Promise<BrevoSyncLogResponse> => {
  try {
    let url = `/api/brevo/sync/log/${id}?lineas=${lineas}`;
    if (soloErrores) url += '&soloErrores=true';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching log for execution ${id}:`, error);
    throw error;
  }
};
